/**
 * 🔥 DFCL — DRAGONIOUS FLAME CARD LEGENDS · Collection Book (Phase 1)
 *
 * Canon: /app/memory/DRAGONIOUS_FLAME_CARD_LEGENDS_MASTER_FEB2026.md
 *
 * Read-only browseable catalog. No deck builder, no match runtime — those
 * ship in Phase 2/3. Every card renders with a procedural placeholder face
 * (golden frame, dual-element badges) since real art hasn't landed for
 * most cards yet.
 *
 * Discovery-First: the /dfcl route is currently marked BETA. Access
 * gating (hero unlock / story beat) will land when the owner picks it.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PortraitUpkeepPrompt from "./PortraitUpkeepPrompt";
import DiscoveryReveal from "./DiscoveryReveal";
import { playRareUnlockCry } from "@/utils/rareUnlockCry";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_UNLOCKED_KEY = "dfcl_seen_unlocked_v1";

function loadSeenUnlocked() {
  try {
    const raw = localStorage.getItem(SEEN_UNLOCKED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch { return new Set(); }
}
function saveSeenUnlocked(set) {
  try {
    localStorage.setItem(SEEN_UNLOCKED_KEY, JSON.stringify([...set]));
  } catch { /* storage unavailable — reveal will replay next load, harmless */ }
}

// ────────────────────── Small utility bits ──────────────────────
const RARITY_ORDER = ["Rare", "Epic", "Legendary", "Mythic"];

// ────────────────────── Root ──────────────────────
export default function DfclCollectionBook() {
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [archetypes, setArchetypes] = useState([]);
  const [cards, setCards] = useState([]);
  const [binderCards, setBinderCards] = useState([]);
  const [binderSummary, setBinderSummary] = useState(null);
  const [activeArchetype, setActiveArchetype] = useState(null);
  const [activeRarity, setActiveRarity] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealQueue, setRevealQueue] = useState([]);
  const [unopenedPacks, setUnopenedPacks] = useState([]);
  const [openingCardId, setOpeningCardId] = useState(null);

  async function loadUnopenedPacks() {
    try {
      const r = await axios.get(`${API}/dfcl/unopened-packs`);
      setUnopenedPacks(r.data.packs || []);
    } catch { /* unauth / empty — hide section */ }
  }
  async function openPack(cardId) {
    if (openingCardId) return;
    setOpeningCardId(cardId);
    try {
      const r = await axios.post(`${API}/dfcl/binder/open-pack`, { card_id: cardId });
      const d = r.data || {};
      // 🐦‍🔥 Rare Unlock Cry — fires for Purple / Orange / Gold pulls.
      playRareUnlockCry(d.rarity);
      const { toast } = await import("sonner");
      toast.success(
        `🔥 ${d.is_first_unlock ? "NEW " : ""}${d.card_name}`,
        {
          description: d.is_first_unlock
            ? `Added to your binder. Rarity: ${String(d.rarity).toUpperCase()}.`
            : `+1 copy added (×${d.copies}). Rarity: ${String(d.rarity).toUpperCase()}.`,
          duration: 6000,
        },
      );
      // Refresh both the binder and the unopened list so the UI stays in sync.
      await Promise.all([
        axios.get(`${API}/dfcl/binder`).then((b) => {
          setBinderCards(b.data.cards || []);
          setBinderSummary(b.data.summary || null);
        }),
        loadUnopenedPacks(),
      ]);
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e?.response?.data?.detail || "Could not open the pack.");
    } finally {
      setOpeningCardId(null);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, a, b] = await Promise.all([
          axios.get(`${API}/dfcl/summary`),
          axios.get(`${API}/dfcl/archetypes`),
          axios.get(`${API}/dfcl/binder`),
        ]);
        if (cancelled) return;
        setSummary(s.data);
        setArchetypes(a.data.archetypes || []);
        setBinderCards(b.data.cards || []);
        setBinderSummary(b.data.summary || null);

        // 🔓 Diff freshly-unlocked binder cards against what the player
        // has already seen (localStorage). Anything newly unlocked gets
        // enqueued for a one-shot flip + shimmer reveal.
        const binder = b.data.cards || [];
        const unlockedNow = new Set(
          binder.filter((c) => !c.locked).map((c) => c.id),
        );
        const seen = loadSeenUnlocked();
        const fresh = binder.filter(
          (c) => !c.locked && !seen.has(c.id),
        );
        if (fresh.length > 0) {
          setRevealQueue(fresh);
        }
        // Mark everything currently unlocked as seen so the overlay only
        // fires once per card per browser.
        saveSeenUnlocked(unlockedNow);
        // 📦 Load any Save-for-Later DFCL packs so the binder can show
        // them next to the discovery grid.
        loadUnopenedPacks();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("DFCL summary/binder load failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (activeArchetype) params.set("archetype", activeArchetype);
    if (activeRarity)    params.set("rarity",    activeRarity);
    (async () => {
      try {
        const r = await axios.get(`${API}/dfcl/catalog?${params.toString()}`);
        if (cancelled) return;
        setCards(r.data.cards || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeArchetype, activeRarity]);

  return (
    <div className="min-h-screen bg-[#08070C] text-white" data-testid="dfcl-collection-book">
      {/* Header */}
      <div className="border-b-2 border-[#FCD34D]/40 bg-gradient-to-b from-[#1a0e0e] to-[#08070C]">
        <div className="max-w-6xl mx-auto px-4 py-5 flex items-center gap-3">
          <button
            onClick={() => nav(-1)}
            data-testid="dfcl-back"
            className="text-neutral-400 hover:text-white text-sm"
          >← Back</button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FCD34D]">Dragonious Flame Card Legends</div>
              <span
                className="text-[9px] uppercase tracking-widest px-2 py-0.5 border border-[#F87171]/60 text-[#F87171] rounded-sm"
                data-testid="dfcl-beta-badge"
              >Beta · Coming Soon</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl mt-1 text-white">
              🔥 Collection Book
            </h1>
            <p className="text-[11px] text-neutral-400 mt-1">
              Read-only preview · {summary?.total_cards ?? "—"} cards indexed ·
              deck builder + 1v1 match runtime coming in later phases.
              {binderSummary?.total > 0 && (
                <> · 🔒 Binder <span className="text-[#FCD34D] tabular-nums" data-testid="dfcl-binder-count">{binderSummary.unlocked}/{binderSummary.total}</span></>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        {/* Sidebar filters */}
        <aside className="space-y-5" data-testid="dfcl-filters">
          <FilterBlock
            title="Archetype"
            options={archetypes.map((a) => ({ id: a.id, label: a.label, emoji: a.emoji, hex: a.hex }))}
            active={activeArchetype}
            onPick={setActiveArchetype}
            testidPrefix="dfcl-archetype-"
          />
          <FilterBlock
            title="Rarity"
            options={RARITY_ORDER.map((r) => ({ id: r, label: r }))}
            active={activeRarity}
            onPick={setActiveRarity}
            testidPrefix="dfcl-rarity-"
          />
          <RulesPanel rules={summary?.rules} />
        </aside>

        {/* Card grid */}
        <div>
          {binderCards.length > 0 && (
            <div className="mb-6" data-testid="dfcl-binder-section">
              <div className="flex items-baseline justify-between mb-2">
                <div>
                  <div className="text-[9px] uppercase tracking-[0.3em] text-[#FCD34D]">🔒 Binder · Discovery Cards</div>
                  <div className="text-[10px] text-neutral-500 mt-0.5">
                    Your personal binder. Buy from the Aethelgard Bazaar, win via PvP steals, or defeat specific bosses to add copies. View-only outside battle · battle-ready + tradable.
                  </div>
                </div>
                {binderSummary && (
                  <div className="text-[10px] text-neutral-400 tabular-nums text-right" data-testid="dfcl-binder-summary">
                    <div>{binderSummary.unlocked}/{binderSummary.total} unlocked</div>
                    {binderSummary.total_copies > 0 && (
                      <div className="text-[#FCD34D]">×{binderSummary.total_copies} copies owned</div>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="dfcl-binder-grid">
                {binderCards.map((c) => (
                  <CardTile key={c.id} card={c} onClick={() => setSelected(c)} />
                ))}
              </div>
            </div>
          )}

          {/* 📦 Owner directive Feb 2026 — Save-for-Later packs. Spin
              wheel DFCL wins land here when the player chose "Save for
              Later" instead of "Use Now". One-tap Open opens the pack. */}
          {unopenedPacks.length > 0 && (
            <div
              className="mb-4 border border-rose-800/60 bg-rose-950/20 p-3"
              data-testid="dfcl-unopened-packs"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.35em] text-rose-300">
                  📦 Unopened Packs ·{" "}
                  <span className="tabular-nums text-rose-200" data-testid="dfcl-unopened-count">
                    {unopenedPacks.reduce((s, p) => s + (p.qty || 0), 0)}
                  </span>
                </div>
                <div className="text-[9px] text-neutral-500 italic">
                  Open any time
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {unopenedPacks.map((p) => (
                  <div
                    key={p.card_id}
                    data-testid={`dfcl-pack-${p.card_id}`}
                    className="border border-rose-700/50 bg-black/50 p-2 flex flex-col items-center gap-1"
                  >
                    <div className="text-2xl">{p.icon || "🔥"}</div>
                    <div className="text-[11px] font-bold text-rose-100 text-center leading-tight">
                      {p.name}
                    </div>
                    <div className="text-[9px] uppercase tracking-widest text-rose-300/70">
                      {String(p.rarity).toUpperCase()} · ×{p.qty}
                    </div>
                    <button
                      onClick={() => openPack(p.card_id)}
                      disabled={openingCardId === p.card_id}
                      data-testid={`dfcl-pack-open-${p.card_id}`}
                      className="w-full bg-[#D4AF37] hover:bg-[#F1C84B] disabled:opacity-50 text-black font-bold uppercase tracking-widest text-[10px] py-1 transition"
                    >
                      {openingCardId === p.card_id ? "…" : "Open"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {binderCards.length > 0 && (
            <div className="text-[9px] uppercase tracking-[0.3em] text-neutral-500 mb-2">Public Preview</div>
          )}
          {loading && cards.length === 0
            ? <div className="text-neutral-500 text-sm py-8">Loading catalog…</div>
            : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="dfcl-card-grid">
                {cards.map((c) => (
                  <CardTile key={c.id} card={c} onClick={() => setSelected(c)} />
                ))}
                {cards.length === 0 && (
                  <div className="col-span-full text-neutral-500 text-sm py-8 text-center">
                    No cards match those filters yet.
                  </div>
                )}
              </div>
            )}
        </div>
      </div>

      {selected && <CardDetailModal card={selected} onClose={() => setSelected(null)} />}

      {/* 🔓 Discovery Reveal — pops when the binder diff finds a newly
          unlocked card. The queue drains one card per Continue tap. */}
      {revealQueue.length > 0 && (
        <DiscoveryReveal
          card={revealQueue[0]}
          onContinue={() => setRevealQueue((q) => q.slice(1))}
        />
      )}
    </div>
  );
}

// ────────────────────── Sidebar filter block ──────────────────────
function FilterBlock({ title, options, active, onPick, testidPrefix }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.25em] text-neutral-500 mb-2">{title}</div>
      <div className="flex flex-col gap-1">
        <button
          onClick={() => onPick(null)}
          data-testid={`${testidPrefix}all`}
          className={`text-left text-xs px-2 py-1.5 border transition ${
            !active
              ? "border-[#FCD34D] bg-[#FCD34D]/10 text-white"
              : "border-zinc-800 text-neutral-400 hover:border-zinc-700"
          }`}
        >All</button>
        {options.map((o) => (
          <button
            key={o.id}
            onClick={() => onPick(o.id)}
            data-testid={`${testidPrefix}${o.id}`}
            className={`text-left text-xs px-2 py-1.5 border transition ${
              active === o.id
                ? "border-[#FCD34D] bg-[#FCD34D]/10 text-white"
                : "border-zinc-800 text-neutral-400 hover:border-zinc-700"
            }`}
            style={active === o.id && o.hex ? { boxShadow: `inset 0 0 0 1px ${o.hex}66` } : undefined}
          >
            {o.emoji ? <span className="mr-1.5">{o.emoji}</span> : null}
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ────────────────────── Rules panel (right sidebar summary) ──────────────────────
function RulesPanel({ rules }) {
  if (!rules) return null;
  return (
    <div className="border border-zinc-900 bg-black/40 p-3" data-testid="dfcl-rules-panel">
      <div className="text-[9px] uppercase tracking-[0.25em] text-[#FCD34D] mb-2">Rules snapshot</div>
      <div className="text-[10px] text-neutral-400 space-y-1 tabular-nums">
        <div>Deck size: <span className="text-white">{rules.deck_main_size}</span></div>
        <div>Finisher side: <span className="text-white">{rules.finisher_side_min}–{rules.finisher_side_max}</span></div>
        <div>Fighters: <span className="text-white">≥ 1 (no cap)</span></div>
        <div>Bench: <span className="text-white">{rules.bench_max}</span></div>
        <div>Hand cap: <span className="text-white">{rules.hand_max}</span></div>
        <div>Tokens 1 → <span className="text-white">{rules.token_max_cap || rules.energy_max_cap}</span></div>
        <div>Fighter base HP: <span className="text-white">{rules.fighter_base_hp.toLocaleString()}</span></div>
        <div>Showtime: <span className="text-white">≤ {Math.round(rules.showtime_hp_pct * 100)}%</span></div>
        <div>Fatality: <span className="text-white">≤ {Math.round(rules.fatality_hp_pct * 100)}%</span></div>
      </div>
    </div>
  );
}

// ────────────────────── Card tile (grid cell) ──────────────────────
function CardTile({ card, onClick }) {
  const p = card.palette || {};
  const r = card.rarity_meta || {};
  const hasArt = Boolean(card.art_url);
  const isLocked = Boolean(card.locked);

  if (isLocked) {
    return (
      <button
        onClick={onClick}
        data-testid={`dfcl-card-${card.id}`}
        className="group relative bg-[#0F0A08] border-2 border-dashed hover:border-[#FCD34D] transition text-left"
        style={{ borderColor: (r.frame_hex || "#3f3f46") + "88" }}
      >
        <div
          className="relative aspect-[3/4] overflow-hidden"
          style={{ background: "repeating-linear-gradient(45deg, #0F0A08 0px, #0F0A08 10px, #141015 10px, #141015 20px)" }}
          data-testid={`dfcl-card-locked-${card.id}`}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-3">
            <div className="text-5xl opacity-50 mb-2">🔒</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Locked</div>
            {card.card_number && (
              <div className="text-[9px] text-neutral-600 mt-1 tabular-nums">#{card.card_number}</div>
            )}
          </div>
          <div className="absolute bottom-1 right-1.5 text-[9px]" style={{ color: r.frame_hex }}>
            {r.stars}
          </div>
        </div>
        <div className="px-2 py-1.5 bg-[#08070C] border-t border-zinc-800">
          <div className="text-[10px] font-bold text-neutral-500 leading-tight uppercase tracking-wide truncate">???</div>
          <div className="text-[9px] text-neutral-600 leading-tight truncate">{r.label || "Unknown"}</div>
        </div>
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      data-testid={`dfcl-card-${card.id}`}
      className="group relative bg-[#0F0A08] border-2 hover:border-[#FCD34D] transition text-left"
      style={{ borderColor: r.frame_hex || "#3f3f46" }}
    >
      {/* Card face */}
      <div
        className="relative aspect-[3/4] overflow-hidden"
        style={
          hasArt
            ? { background: "#0F0A08" }
            : {
                background: `linear-gradient(135deg, ${p.primary_hex || "#3f3f46"}44 0%, #0F0A08 55%, ${p.secondary_hex || "#3f3f46"}44 100%)`,
              }
        }
      >
        {hasArt ? (
          /* Real card art — the full card graphic (frame + title + rules all baked in) */
          <img
            src={card.art_url}
            alt={card.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid={`dfcl-card-art-${card.id}`}
          />
        ) : (
          <>
            {/* Element badges — top corners */}
            <div className="absolute top-1.5 left-1.5 text-lg drop-shadow-[0_0_4px_rgba(0,0,0,0.9)]">{p.primary_emoji}</div>
            {p.secondary_emoji && p.secondary_emoji !== p.primary_emoji && (
              <div className="absolute top-1.5 right-1.5 text-lg drop-shadow-[0_0_4px_rgba(0,0,0,0.9)]">{p.secondary_emoji}</div>
            )}
            {/* Big center emoji — mono placeholder face */}
            <div className="absolute inset-0 flex items-center justify-center text-5xl opacity-40 group-hover:opacity-70 transition">
              {p.primary_emoji}
            </div>
            {/* Placeholder ribbon */}
            {card.placeholder && (
              <div className="absolute top-1 right-1/2 translate-x-1/2 text-[7px] uppercase tracking-widest text-[#FCD34D]/60">
                placeholder art
              </div>
            )}
            {/* Rarity stars — bottom-right */}
            <div className="absolute bottom-1 right-1.5 text-[9px]" style={{ color: r.frame_hex }}>
              {r.stars}
            </div>
            {/* HP counter — bottom-left */}
            {card.hp && (
              <div className="absolute bottom-1 left-1.5 text-[9px] tabular-nums text-red-300">
                ♥ {card.hp.toLocaleString()}
              </div>
            )}
          </>
        )}
      </div>
      {/* Title strip */}
      <div className="px-2 py-1.5 bg-[#08070C] border-t border-zinc-800">
        <div className="text-[10px] font-bold text-white leading-tight uppercase tracking-wide truncate">{card.name}</div>
        <div className="text-[9px] text-neutral-500 leading-tight truncate">{card.subtitle || card.card_class || (card.card_number ? `#${card.card_number}` : "\u00A0")}</div>
      </div>
      {/* Copies owned badge — bottom-right strip, only shown when the player owns ≥1 copy */}
      {card.copies > 0 && (
        <div
          className="absolute top-1.5 right-1.5 bg-[#08070C]/90 border border-[#FCD34D] px-1.5 py-0.5 text-[9px] font-bold tabular-nums text-[#FCD34D]"
          data-testid={`dfcl-card-copies-${card.id}`}
        >
          ×{card.copies}
        </div>
      )}
      {/* Cost pip — top-left overlay (hidden when real art is used, cost is baked in) */}
      {!hasArt && (
        <div className="absolute top-1.5 left-1/2 -translate-x-1/2 bg-[#08070C]/90 border border-[#FCD34D]/60 px-2 py-0.5 text-[9px] font-bold tabular-nums text-[#FCD34D]">
          {card.cost_essence ?? 0} 🔥
        </div>
      )}
    </button>
  );
}

// ────────────────────── Card detail modal ──────────────────────
function CardDetailModal({ card, onClose }) {
  const p = card.palette || {};
  const r = card.rarity_meta || {};
  const a = card.archetype_meta || {};
  const isLocked = Boolean(card.locked);
  const [showUpkeep, setShowUpkeep] = useState(false);

  if (isLocked) {
    return (
      <div
        className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="dfcl-card-detail"
      >
        <div
          className="max-w-md w-full bg-[#0F0A08] border-2 border-dashed"
          style={{ borderColor: (r.frame_hex || "#f59e0b") + "88" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 text-center">
            <div className="text-6xl mb-3">🔒</div>
            <div className="text-[9px] uppercase tracking-widest text-neutral-500">{card.id}{card.card_number ? ` · #${card.card_number}` : ""}</div>
            <div className="font-display text-2xl font-black leading-tight text-neutral-400 mt-1">???</div>
            <div className="mt-2 text-[10px] uppercase tracking-widest" style={{ color: r.frame_hex }}>{r.stars} {card.rarity}</div>
            <div className="mt-5 text-[12px] text-neutral-300 leading-relaxed">
              {card.lock_hint || "Locked — unlock to reveal."}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="dfcl-card-close"
            className="w-full bg-zinc-900 hover:bg-zinc-800 text-white py-2 uppercase tracking-widest text-xs border-t border-zinc-800"
          >Close</button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="dfcl-card-detail"
    >
      <div
        className="max-w-lg w-full bg-[#0F0A08] border-2 max-h-[90vh] overflow-y-auto"
        style={{ borderColor: r.frame_hex || "#f59e0b" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Art banner (binder cards with real art) */}
        {card.art_url && (
          <div className="bg-black flex justify-center" data-testid="dfcl-card-art-banner">
            <img
              src={card.art_url}
              alt={card.name}
              className="max-h-[60vh] w-auto block"
              data-testid={`dfcl-card-art-${card.id}`}
            />
          </div>
        )}

        {/* Header */}
        <div
          className="p-4"
          style={{
            background: `linear-gradient(135deg, ${p.primary_hex}33 0%, #0F0A08 60%, ${p.secondary_hex}33 100%)`,
          }}
        >
          <div className="flex items-start gap-3">
            {!card.art_url && (
              <div className="text-4xl">{p.primary_emoji}{p.secondary_emoji !== p.primary_emoji ? p.secondary_emoji : ""}</div>
            )}
            <div className="flex-1">
              <div className="text-[9px] uppercase tracking-widest text-neutral-500">{card.id} · {card.set_code}{card.card_number ? ` · #${card.card_number}` : ""}</div>
              <div className="font-display text-2xl font-black leading-tight text-white">{card.name}</div>
              {card.subtitle && <div className="text-xs text-neutral-400 italic">{card.subtitle}</div>}
              {(card.rarity || a.label || card.cost_essence != null || card.hp || card.atk || card.def) && (
                <div className="mt-1 flex items-center gap-2 flex-wrap text-[10px]">
                  {card.rarity && <span className="uppercase tracking-widest" style={{ color: r.frame_hex }}>{r.stars} {card.rarity}</span>}
                  {a.label && <span className="text-neutral-500">· {a.emoji} {a.label}</span>}
                  {card.cost_essence != null && <span className="text-[#FCD34D]">· {card.cost_essence} 🔥 Essence</span>}
                  {card.hp && <span className="text-red-300 tabular-nums">· ♥ {card.hp.toLocaleString()} HP</span>}
                  {card.atk && <span className="text-orange-300 tabular-nums">· ⚔ {card.atk.toLocaleString()} ATK</span>}
                  {card.def && <span className="text-cyan-300 tabular-nums">· 🛡 {card.def.toLocaleString()} DEF</span>}
                  {card.copies > 0 && (
                    <span className="text-[#FCD34D] tabular-nums" data-testid="dfcl-card-copies-owned">
                      · 📚 ×{card.copies} owned
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Costs / restrictions */}
        {(card.alternate_cost || card.additional_costs || card.upkeep_nexus_hp) && (
          <div className="px-4 py-2 border-y border-zinc-900 text-[11px] text-neutral-400 space-y-0.5" data-testid="dfcl-card-costs">
            {card.alternate_cost && (
              <div>💠 <span className="text-neutral-300">Alternate cost:</span> {card.alternate_cost.amount} {card.alternate_cost.type.replace("_", " ")}</div>
            )}
            {card.upkeep_nexus_hp && (
              <div>🩸 <span className="text-neutral-300">Upkeep:</span> {card.upkeep_nexus_hp} Nexus HP per turn</div>
            )}
            {card.additional_costs?.nexus_hp && (
              <div>🩸 <span className="text-neutral-300">Extra cost:</span> {card.additional_costs.nexus_hp} Nexus HP</div>
            )}
            {card.additional_costs?.skip_next_turn && (
              <div>⏸ <span className="text-neutral-300">Drawback:</span> Skips next turn (Stasis)</div>
            )}
          </div>
        )}

        {/* Abilities */}
        <div className="p-4 space-y-3">
          {card.rules_md && (
            <div
              data-testid="dfcl-card-rules-md"
              className="whitespace-pre-wrap text-[12px] leading-relaxed text-neutral-200 max-h-[55vh] overflow-y-auto pr-1"
            >{card.rules_md}</div>
          )}
          {(card.abilities || []).map((ab, i) => (
            <AbilityBlock key={i} ability={ab} />
          ))}

          {card.flavor && (
            <div className="pt-2 border-t border-zinc-900 text-[11px] italic text-neutral-500">"{card.flavor}"</div>
          )}
          {card.designer_hint && (
            <div className="bg-black/50 border-l-2 border-[#FCD34D] pl-2 py-1 text-[10px] text-neutral-400">
              <span className="text-[#FCD34D] font-bold uppercase tracking-widest mr-1">Designer hint:</span>
              {card.designer_hint}
            </div>
          )}
          {card.director_rule && (
            <div className="bg-red-900/20 border-l-2 border-red-500 pl-2 py-1 text-[10px] text-red-200">
              <span className="text-red-400 font-bold uppercase tracking-widest mr-1">Director's rule:</span>
              {card.director_rule}
            </div>
          )}
          {card.tutor_restrictions?.length > 0 && (
            <div className="text-[10px] text-orange-300">
              🚫 Tutor restrictions: {card.tutor_restrictions.join(", ")}
            </div>
          )}
          {card.special_notes && (
            <div className="bg-[#FCD34D]/5 border border-[#FCD34D]/30 p-2 text-[10px] text-neutral-300">
              📝 {card.special_notes}
            </div>
          )}

          {/* 🪤 Interactive: Lifeless Portrait upkeep demo. */}
          {card.id === "DFCL-334" && (
            <button
              onClick={() => setShowUpkeep(true)}
              data-testid="dfcl-try-upkeep-btn"
              className="w-full mt-1 bg-[#7c3aed]/90 hover:bg-[#8b5cf6] text-white font-bold uppercase tracking-widest text-[11px] py-2 transition border border-[#a78bfa]/60"
            >
              🪤 Try the Upkeep Phase
            </button>
          )}
        </div>

        <button
          onClick={onClose}
          data-testid="dfcl-card-close"
          className="w-full bg-zinc-900 hover:bg-zinc-800 text-white py-2 uppercase tracking-widest text-xs border-t border-zinc-800"
        >Close</button>
      </div>
      {showUpkeep && (
        <PortraitUpkeepPrompt onClose={() => setShowUpkeep(false)} />
      )}
    </div>
  );
}

// ────────────────────── Ability block (Fatality / Harmony / etc) ──────────────────────
const KIND_META = {
  ultimate:        { label: "Ultimate Ability",   hex: "#FCD34D", emoji: "👑" },
  fatality:        { label: "Fatality",           hex: "#DC2626", emoji: "💀" },
  harmony:         { label: "Harmony",            hex: "#F472B6", emoji: "🎵" },
  passive:         { label: "Passive Ability",    hex: "#60A5FA", emoji: "🌀" },
  active_trigger:  { label: "Active Trigger",     hex: "#A855F7", emoji: "⚡" },
  shift_skill:     { label: "Shift Skill",        hex: "#B91C1C", emoji: "🔄" },
  continuous:      { label: "Continuous Effect",  hex: "#22C55E", emoji: "♾️" },
  trap:            { label: "Trap Trigger",       hex: "#57534E", emoji: "🪤" },
  quick_cast:      { label: "Quick-Cast",         hex: "#38BDF8", emoji: "⚡" },
  ritual:          { label: "Ritual",             hex: "#A855F7", emoji: "🕯️" },
};
function AbilityBlock({ ability }) {
  const m = KIND_META[ability.kind] || { label: ability.kind, hex: "#78716c", emoji: "✨" };
  return (
    <div
      className="bg-black/40 border-l-2 pl-3 pr-2 py-2"
      style={{ borderColor: m.hex }}
      data-testid={`dfcl-ability-${ability.kind}`}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm">{m.emoji}</span>
        <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: m.hex }}>{m.label}</span>
        {ability.name && <span className="text-xs text-white font-bold">· {ability.name}</span>}
      </div>
      {ability.trigger && (
        <div className="text-[10px] text-neutral-400 mt-1"><span className="uppercase tracking-widest text-neutral-500">Trigger:</span> {ability.trigger}</div>
      )}
      <div className="text-[11px] text-neutral-200 mt-1 leading-snug">{ability.effect_text}</div>
      {ability.flags?.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {ability.flags.map((f) => (
            <span key={f} className="text-[8px] uppercase tracking-widest bg-zinc-900/80 text-neutral-400 px-1.5 py-0.5 border border-zinc-800">
              {f.replaceAll("_", " ")}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
